import {
  AfterViewInit,
  Component,
  ElementRef,
  Input,
  OnDestroy,
  OnInit,
  QueryList,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { FormGroup } from '@angular/forms';
import { GridStack } from 'gridstack';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { combineLatest, distinctUntilChanged, map, Subject, takeUntil } from 'rxjs';
import { v4 as uuidv4 } from 'uuid';
import {
  CreateDashboardMutation,
  CreateDashboardMutationVariables,
  DashboardMetadata,
  EditDashboardMutation,
  EditDashboardMutationVariables,
  GetPreSignedUrlQuery,
  GetPreSignedUrlQueryVariables,
  PreSignedUrl,
  PreSignedUrlResponse,
} from '../../../../API';
import { CreateDashboard, EditDashboard } from '../../../../graphql/mutations';
import { GetPreSignedUrl } from '../../../../graphql/queries';
import { ApiService } from '../../../shared/services/api.service';
import { S3StorageService } from '../../../shared/services/s3-storage.service';
import { UserManagementService } from '../../../shared/services/user-management.service';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NzModalRef } from 'ng-zorro-antd/modal';
import { DashboardWidget, WidgetCoordinates } from '../../models/dashboard';
import { ChartMetadataExtended, DataArrays } from '../../models/charts';
import { ChartDataService } from '../../services/chart-data.service';
import { ChartPreviewComponent } from '../chart-preview/chart-preview.component';

@Component({
  selector: 'app-dashboard-preview',
  templateUrl: './dashboard-preview.component.html',
  styleUrl: './dashboard-preview.component.scss',
})
export class DashboardPreviewComponent implements AfterViewInit, OnInit, OnDestroy {
  @ViewChild('gridContainer', { static: false }) gridContainer!: ElementRef;
  @ViewChildren(ChartPreviewComponent) chartPreviewComponent?: QueryList<ChartPreviewComponent>;
  @ViewChildren('widgetContent', { read: ElementRef }) widgetElements!: QueryList<ElementRef>;

  @Input() dashboardDetails?: FormGroup;
  @Input() activeDashboardData?: DashboardMetadata;
  @Input() isEditMode = false;

  private destroy$ = new Subject<void>();

  grid?: GridStack;
  widgets: DashboardWidget[] = [];
  orgId = '';
  username = '';
  isLoading = false;
  spinnerContent = '';

  constructor(
    private chartDataService: ChartDataService,
    private apiService: ApiService,
    private userManagementService: UserManagementService,
    private nzMessageService: NzMessageService,
    private s3Service: S3StorageService,
    private nzModalRef: NzModalRef,
  ) {}

  ngOnInit(): void {
    this.orgId = this.userManagementService.getOrgId();
    this.username = this.userManagementService.getEmail();

    combineLatest([this.chartDataService.dashboardCharts$, this.chartDataService.dashboardChartData$])
      .pipe(
        map(([selectedCharts, allChartsResultData]) => ({ selectedCharts, allChartsResultData })),
        distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr)),
        takeUntil(this.destroy$),
      )
      .subscribe(({ selectedCharts, allChartsResultData }) => {
        if (!selectedCharts.length) {
          return;
        }

        const newChart = selectedCharts[selectedCharts.length - 1];
        const chartData = allChartsResultData[newChart.chartId];
        this.addWidget(newChart, chartData);
      });
  }

  ngAfterViewInit(): void {
    this.grid = GridStack.init({ float: true, column: 24 });
    this.activateExistingWidgets();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private activateExistingWidgets(): void {
    if (!this.grid || !this.gridContainer) {
      return;
    }

    this.widgets.forEach((widget) => {
      const el = this.gridContainer.nativeElement.querySelector(`[gs-id="${widget.chartId}"]`);
      if (el) {
        this.grid?.makeWidget(el);
      }
    });
  }

  addWidget(chart: ChartMetadataExtended, chartData: DataArrays, existingWidget?: WidgetCoordinates): void {
    const fallbackY = this.widgets.length * 6;
    const newWidget: DashboardWidget = {
      id: existingWidget ? existingWidget.id : chart.chartId,
      chartId: chart.chartId,
      x: existingWidget ? existingWidget.x : 0,
      y: existingWidget ? existingWidget.y : fallbackY,
      w: existingWidget ? existingWidget.w : 12,
      h: existingWidget ? existingWidget.h : 6,
      chartType: chart.chartType,
      chartData: chartData,
      chartConfig: JSON.parse(chart.config || '{}'),
    };

    const isWidgetAlreadyExist = this.widgets.some((item) => item.chartId === chart.chartId);
    if (isWidgetAlreadyExist) {
      return;
    }

    this.widgets.push(newWidget);

    setTimeout(() => {
      if (!this.grid || !this.gridContainer) {
        return;
      }

      const el = this.gridContainer.nativeElement.querySelector(`[gs-id="${chart.chartId}"]`);
      if (el) {
        this.grid.makeWidget(el);
      }
    }, 250);
  }

  removeWidget(widgetId: string): void {
    const existingSelectedCharts = this.chartDataService.selectedChartsForDashboard.value;
    const filteredCharts = existingSelectedCharts.filter((item) => item.chartId !== widgetId);
    this.chartDataService.selectedChartsForDashboard.next(filteredCharts);

    const widgetEl = this.gridContainer?.nativeElement?.querySelector(`.grid-stack-item[gs-id="${widgetId}"]`);
    if (widgetEl && this.grid) {
      this.grid.removeWidget(widgetEl);
    }

    this.widgets = this.widgets.filter((w) => w.chartId !== widgetId);
  }

  private async captureDashboard(): Promise<HTMLCanvasElement> {
    const dashboard = this.gridContainer.nativeElement;
    const elementWidth = dashboard.offsetWidth + 20;
    const elementHeight = dashboard.offsetHeight + 20;

    return html2canvas(dashboard, {
      scale: 2,
      height: elementHeight,
      width: elementWidth,
    });
  }

  private createDashboardPdf(canvas: HTMLCanvasElement): Blob {
    const imageData = canvas.toDataURL('image/png');
    const width = canvas.width;
    const height = canvas.height;
    const pdfWidthMm = width / 3.7795275591;
    const pdfHeightMm = height / 3.7795275591;

    const pdf = new jsPDF({
      orientation: pdfWidthMm > pdfHeightMm ? 'l' : 'p',
      unit: 'mm',
      format: [pdfWidthMm, pdfHeightMm],
    });

    pdf.addImage(imageData, 'PNG', 0, 0, pdfWidthMm, pdfHeightMm);
    return pdf.output('blob');
  }

  private createDashboardImage(canvas: HTMLCanvasElement): Promise<Blob> {
    return new Promise((resolve, reject) => {
      canvas.toBlob((value) => {
        if (value) {
          resolve(value);
          return;
        }
        reject(new Error('Unable to generate dashboard image'));
      }, 'image/png');
    });
  }

  private async saveDashboardAssets(imageUrl: string, pdfUrl: string): Promise<void> {
    this.spinnerContent = 'Capturing dashboard preview...';
    const canvas = await this.captureDashboard();

    this.spinnerContent = 'Preparing dashboard files...';
    const imageBlob = await this.createDashboardImage(canvas);
    const pdfBlob = this.createDashboardPdf(canvas);

    this.spinnerContent = 'Uploading dashboard files...';
    await Promise.all([this.s3Service.uploadPng(imageUrl, imageBlob), this.s3Service.uploadPdf(pdfUrl, pdfBlob)]);
  }

  async getPresignedUrlForImage(dashboardId: string): Promise<PreSignedUrlResponse> {
    const input: PreSignedUrl = {
      contentType: 'image/png',
      bucketSuffix: 'image-storage',
      s3keyName: `images/dashboard/${this.orgId}/${dashboardId}/dashboard.png`,
    };

    const response = await this.apiService.query<GetPreSignedUrlQuery, GetPreSignedUrlQueryVariables>(GetPreSignedUrl, {
      input,
    });

    const preSigned = response.data?.GetPreSignedUrl;

    if (!preSigned?.putUrl || !preSigned?.deleteUrl || preSigned.statusCode !== 200) {
      this.nzMessageService.error('Failed to get pre-signed URL, please try again');
      throw new Error('Failed to get pre-signed URL');
    }

    return preSigned;
  }

  async getPresignedUrlForPdf(dashboardId: string): Promise<PreSignedUrlResponse> {
    const input: PreSignedUrl = {
      contentType: 'application/pdf',
      bucketSuffix: 'image-storage',
      s3keyName: `images/dashboard/${this.orgId}/${dashboardId}/dashboard.pdf`,
    };

    const response = await this.apiService.query<GetPreSignedUrlQuery, GetPreSignedUrlQueryVariables>(GetPreSignedUrl, {
      input,
    });

    const preSigned = response.data?.GetPreSignedUrl;

    if (!preSigned?.putUrl || !preSigned?.deleteUrl || preSigned.statusCode !== 200) {
      this.nzMessageService.error('Failed to get pre-signed URL, please try again');
      throw new Error('Failed to get pre-signed URL');
    }

    return preSigned;
  }

  async saveDashboardDetails(dashboardId: string): Promise<void> {
    const layout = this.grid?.save(false) || [];
    const { name = '', description = '' } = this.dashboardDetails?.value || {};

    await this.apiService.query<CreateDashboardMutation, CreateDashboardMutationVariables>(CreateDashboard, {
      input: {
        dashboardId,
        chartIds: this.widgets.map((widget) => widget.chartId),
        orgId: this.orgId,
        createdBy: this.username,
        lastModifiedBy: this.username,
        layout: JSON.stringify(layout),
        name,
        description,
      },
    });
  }

  async updateDashboardDetails(dashboardId: string): Promise<void> {
    const layout = this.grid?.save(false) || [];
    const { name = '', description = '' } = this.dashboardDetails?.value || {};

    await this.apiService.query<EditDashboardMutation, EditDashboardMutationVariables>(EditDashboard, {
      input: {
        dashboardId,
        chartIds: this.widgets.map((widget) => widget.chartId),
        orgId: this.orgId,
        lastModifiedBy: this.username,
        layout: JSON.stringify(layout),
        name,
        description,
      },
    });
  }

  async saveDashboard(): Promise<void> {
    const dashboardId = uuidv4();

    this.isLoading = true;
    this.spinnerContent = 'Preparing dashboard...';

    const removeButtonCollection = document.getElementsByClassName('remove-widget-btn');
    Array.from(removeButtonCollection).forEach((element) => {
      (element as HTMLElement).style.display = 'none';
    });

    let preSignedImage: PreSignedUrlResponse | undefined;
    let preSignedPdf: PreSignedUrlResponse | undefined;

    try {
      this.spinnerContent = 'Getting upload URLs...';
      [preSignedImage, preSignedPdf] = await Promise.all([
        this.getPresignedUrlForImage(dashboardId),
        this.getPresignedUrlForPdf(dashboardId),
      ]);

      const imageUrl = preSignedImage?.putUrl;
      const pdfUrl = preSignedPdf?.putUrl;
      if (!imageUrl || !pdfUrl) {
        throw new Error('Missing dashboard upload URL');
      }

      await this.saveDashboardAssets(imageUrl, pdfUrl);

      this.spinnerContent = 'Saving dashboard details...';
      await this.saveDashboardDetails(dashboardId);
      this.nzMessageService.success('Dashboard saved successfully');
      this.nzModalRef.triggerOk();
    } catch (error: any) {
      if (preSignedImage?.deleteUrl || preSignedPdf?.deleteUrl) {
        try {
          this.spinnerContent = 'Cleaning up dashboard files...';
          await Promise.all([
            preSignedImage?.deleteUrl
              ? this.s3Service.deletePresignedChartAndDashboard(preSignedImage.deleteUrl)
              : Promise.resolve(),
            preSignedPdf?.deleteUrl
              ? this.s3Service.deletePresignedChartAndDashboard(preSignedPdf.deleteUrl)
              : Promise.resolve(),
          ]);
        } catch {
          console.warn('Dashboard cleanup failed');
        }
      }

      this.nzMessageService.error(`Failed to save dashboard due to error ${error?.message || error}`);
    } finally {
      this.isLoading = false;
      this.spinnerContent = '';
      Array.from(removeButtonCollection).forEach((element) => {
        (element as HTMLElement).style.display = 'block';
      });
    }
  }

  async updateDashboard(dashboardId: string): Promise<void> {
    this.isLoading = true;
    this.spinnerContent = 'Preparing dashboard update...';

    const removeButtonCollection = document.getElementsByClassName('remove-widget-btn');
    Array.from(removeButtonCollection).forEach((element) => {
      (element as HTMLElement).style.display = 'none';
    });

    let preSignedImage: PreSignedUrlResponse | undefined;
    let preSignedPdf: PreSignedUrlResponse | undefined;

    try {
      this.spinnerContent = 'Getting upload URLs...';
      [preSignedImage, preSignedPdf] = await Promise.all([
        this.getPresignedUrlForImage(dashboardId),
        this.getPresignedUrlForPdf(dashboardId),
      ]);

      const imageUrl = preSignedImage?.putUrl;
      const pdfUrl = preSignedPdf?.putUrl;
      if (!imageUrl || !pdfUrl) {
        throw new Error('Missing dashboard upload URL');
      }

      await this.saveDashboardAssets(imageUrl, pdfUrl);

      this.spinnerContent = 'Saving dashboard details...';
      await this.updateDashboardDetails(dashboardId);
      this.nzMessageService.success('Dashboard updated successfully');
      this.nzModalRef.triggerOk();
    } catch (error: any) {
      if (preSignedImage?.deleteUrl || preSignedPdf?.deleteUrl) {
        try {
          this.spinnerContent = 'Cleaning up dashboard files...';
          await Promise.all([
            preSignedImage?.deleteUrl
              ? this.s3Service.deletePresignedChartAndDashboard(preSignedImage.deleteUrl)
              : Promise.resolve(),
            preSignedPdf?.deleteUrl
              ? this.s3Service.deletePresignedChartAndDashboard(preSignedPdf.deleteUrl)
              : Promise.resolve(),
          ]);
        } catch {
          console.warn('Dashboard cleanup failed');
        }
      }

      this.nzMessageService.error(`Failed to update dashboard due to error ${error?.message || error}`);
    } finally {
      this.isLoading = false;
      this.spinnerContent = '';
      Array.from(removeButtonCollection).forEach((element) => {
        (element as HTMLElement).style.display = 'block';
      });
    }
  }
}
